import type { HelpSection } from '../helpContent';

const DESK: HelpSection['who'] = ['manager', 'admin', 'hr'];

export const STAFF: HelpSection[] = [
  // ---------------- Rotation
  {
    id: 'rotation', group: 'Rotation', title: 'Making today’s rotation', who: DESK, where: ['desk'], pages: ['rotation'],
    body: `
The rotation places everyone working each hour into a station. Pick the day with the day buttons at the top.

### Preview, then create
- Until a rotation is created, managers see a **preview**. Each spot shows only **Filled** or **Short**, with no names, so you can see where you're short and set party hosts, share groups and closed stations first. The counter screen and employees see nothing yet. A printed or emailed preview shows the same.
- **Create rotation** puts names in the spots and makes it the day's rotation. From then on the counter screen and employees (Today screen, "Where you're stationed") see it, and each hour is kept once it's over.
- After that, **Create new rotation…** starts over from the hour going on now. It reshuffles everyone and clears spots changed by hand in the hours still to come. People who left early, called out or were called in stay as they are.

The counter screen can create today's rotation the first time (managers only). Re-creating one is done here on the desktop.

### Hours that are over are locked
Once an hour is over, that hour of the rotation is locked and kept exactly as it was: who was at each station, and their breaks. It's shaded on the board and can't be clicked, here or on the counter screen. The hour going on now can still be changed.
- Changes made later (a mid-day change entered late, a shift changed on the day board, a station's settings) only change the hours still to come. The locked hours keep showing who really worked where.
- Ticking a share group now starts it from the hour going on now; unticking it ends it then. A group that already ended can't start again later the same day.
- Once the whole day is over, its rotation can't be created or re-created.

### How it fills each hour
1. Spots changed by hand stay put (they're "pinned").
2. Stations ticked **Fill all spots first** in [[settings]] (Go Karts) get all their spots first.
3. Then everyone else is spread out: every open station gets one person (in list order) before any station gets a second, then second spots, and so on. So on a short-staffed hour the counters don't take everyone while other stations stay empty. Each spot picks someone without a break that hour where possible, and keeps versatile people free for hard-to-fill stations.
4. Anyone left over who isn't allowed at an empty spot gets one by a reshuffle: someone already placed who *is* allowed there moves into it, and the left-over person takes their old spot (when they're allowed there).
5. A last check trades places so people on a break land on the stations set to "Prefer" for breaks, and off the "Avoid" ones. It only trades between people who can each work the other's spot, so it never leaves a station short.

If someone still can't be placed, **Working but not placed** (under the board) says why, for example *allowed at Admissions (full, and doesn't take overflow); can't work Floater (level 7 isn't ticked in Settings)*. Fix it in [[settings]] (tick their level, or set **Takes overflow** to Yes) or on their own station boxes on Employees.

**Manager and Shift Lead** are filled before every other spot, by level: higher levels always push the others down.
- A store manager (level 9) working always gets Manager 1.
- Then the highest level working gets the next Manager spot, then the Shift Lead spot: an 8 before a 7, a 7 before a 6. With an 8 and a 7 and one Manager spot, the 8 is Manager and the 7 is Shift Lead.
- People at the same level take turns hour by hour. With a 9 and two 8s and one Manager spot, the 9 is Manager and the two 8s swap the Shift Lead spot each hour.
- Whoever isn't in a Manager or Shift Lead spot works the other stations their level allows.

A station's **Max** is never exceeded.

### Reading the board
- Shaded hours are busy. The current hour is highlighted.
- The bar under each name shows that hour's quarters: yellow = break, blue = lunch.
- **Short** = nobody who can work it was free. **Closed** = closed for the day. **→ Party** = that person moved for that hour. **In group 1** = the station is being shared that hour.
- **Working but not placed in a station** lists anyone left over.

### Print and email
**Print rotation** prints just the rotation on one landscape page. The rows and names grow to fill the page, and rows with nothing on them all day (like Party with no host) are left off. **Email…** sends it as a PDF ([[email-pdf]]).
`,
  },
  {
    id: 'rotation-swaps', group: 'Rotation', title: 'Swapping people and pins', who: DESK, where: ['desk'],
    body: `
1. Click a name in the rotation.
2. A bar opens with the people working that hour who can work that spot: "· free", "· now at Laser Tag", or "· part of hour" for someone working only part of it.
3. Click one. They take the spot, and if they were at another station, the two swap.

A changed spot gets a **pin** and stays put, even when the rotation refills around it. To let the rotation choose again, click the spot and press **Remove pin**.

### The change list
At the bottom of the desktop Rotation page, **Changes made by hand to this rotation** lists every swap and removed pin for that day: the time, the hour and spot, who went in and out, and who did it. Changes made on the counter screen show the unlocking person's name with "(counter tablet)". This list is never shown on the counter screen.
`,
  },
  {
    id: 'share-groups', group: 'Rotation', title: 'Share groups (Go Karts, Laser Tag, Pixel…)', who: DESK, where: ['desk'],
    body: `
A share group lets a few people cover several attractions together on a quiet day. Which stations belong to group 1, 2 or 3 is set in [[settings]]. Group 1 is Go Karts, Laser Tag and Pixel to start with.

On the Rotation page, each group with two or more stations has a tick box for that day, above the board:
1. Tick **Group 1** (for example). Its stations come off the rotation and the group's rows take their place: **Group 1 · Spot 1**, **Group 1 · Spot 2**…, with the group's stations listed under **Spot 1**. A station that's shared all day doesn't get a row of its own.
2. Pick how many **people** cover the group that day (1–6). Settings holds the usual number.
3. Optional: pick the hours (**Open** to **Close** means all day). Outside those hours the stations run normally, and while shared their cells read "In group 1".

Only people who can work **every** station in the group are placed there, and the group follows the strictest rules of its stations (full hour, break limits, ★ for the first spot).

A station can be in more than one group. If two ticked groups share a station, it goes with the lower-numbered group. If that leaves the other group with only one station, that group isn't used that day: a yellow line says so and why (for example "Concessions is already in Group 1 today, which leaves only Redemption"). Untick one of the groups, or change the stations in [[settings]].

> Ticking or unticking a group, or changing its hours, clears every hand-changed spot (pin) for that day. Do it before you fine-tune.
`,
  },
  {
    id: 'party-hosts', group: 'Rotation', title: 'Party hosts and other “only when needed” stations', who: DESK, where: ['desk'],
    body: `
Some stations, like Party, are only staffed when needed ("Only when needed" in Settings). They're left out of the automatic rotation.

1. On the Rotation page, the station's row shows **☐ Host?** in every hour. Click the hours you need a host.
2. In the bar that opens, choose **1**, **2** or **3** hosts for that hour, and pick who if you like.
3. **No host this hour** unticks it again.

Hosts are pulled in for that hour only, so the rest of the day doesn't reshuffle. The app takes, in order: someone with no spot, then someone extra beyond what a station needs, then a Floater, then Floor. A Floater or Floor spot left behind shows "→ Party". It avoids giving anyone two host hours in a row, and shows **Short** if nobody fits.

Unticked boxes are hidden on printouts and on the counter screen.
`,
  },
  {
    id: 'closing-stations', group: 'Rotation', title: 'Closing a station for the day', who: DESK, where: ['desk'],
    body: `
At the bottom of the Rotation page, under **Open today**, click a station to close it for this day only (for example, Go Karts down for repairs). Click it again to reopen.

- It closes all day. To close it for part of the day, pick **from** and **until** times next to it.
- Under **When a station closes**, choose **Move only its people** (only the people at that station move) or **Re-rotate everyone from then**. The same choice applies after a mid-day change.

For an overnight party, close the stations you won't run during the night.

Stations an admin has marked **Out of service** in Settings show as "out of service" here and can't be reopened from this page.
`,
  },
  // ---------------- Employees
  {
    id: 'employees-add', group: 'Employees', title: 'Adding employees', who: DESK, where: ['desk'], pages: ['employees'],
    body: `
Each store's managers type in their own people on the **Employees** page. Admins add the managers first on Managers & access. HR can look but not add.

1. Type the **Name** (first and last).
2. Pick the **Level**. It decides which stations they get to start with ([[settings]] shows which).
3. Type their **Mobile number**. It's what they sign in with. The app checks it as you type: it needs all 10 digits, and no two people (at any store) can share a number.
4. Optional: **Copy stations from** another employee to give them the same stations instead of their level's.
5. Press **Add employee** (or Enter).

The form stays ready for the next person and keeps the level and "Copy stations from". A count shows how many people are on the list.

### Rehiring
If the name or number matches someone who was archived, a warning asks **"Is this the same person?"**. Press **Restore … instead** to bring them back with their history. Someone archived at another store can only be brought back by an admin (or a manager of that store).

> Levels 7–9 (managers) can only be added by an admin or the store manager (level 9).

For Operations (techs and office staff), only admins add people. Pick Operations in **Location** first. They get a **Position** instead of stations.
`,
  },
  {
    id: 'employees-edit', group: 'Employees', title: 'Names, levels, stations, mobile and texts', who: DESK, where: ['desk'],
    body: `
People are listed A to Z. To change something, click it and type. It saves when you press Enter or click away.

- **Level**: picking a level resets their station boxes to that level's set. Only an admin or the store manager can make someone a manager or change a manager's level.
- **Station boxes**: click once for ✓ (can work it). Click again for ★ (can also take the first spot, on stations that need one). Click again to clear. Changing a box by hand makes the level **C** (custom).
- **Mobile**: click it to change it. The same checks apply as when adding.
- **Texts** box (under the number): ticked = they get texts. Point at it to see who set it, how and when.
  - Unticking takes effect straight away.
  - Ticking asks you to confirm they've agreed ("Yes, they agreed"). Only do this if they've told you they agree to the text terms. Otherwise ask them to turn texts on in the app.
  - Someone who replied STOP also needs to text START to our number once.

Under each name you'll also find the [[attendance]] button and tags like **Leaving 11/15** or **Moves to Gresham 11/3**.
`,
  },
  {
    id: 'signin-codes', group: 'Employees', title: 'Sign-in codes and PIN resets', who: ['manager', 'admin'], where: ['desk'],
    body: `
Everyone signs in with their own mobile number and a PIN they pick (see [[signing-in]]). The first time on a phone they need a one-time code. It's normally texted to them, but people who said no to texts get it from you.

- **Sign-in code** (shown for someone with no PIN yet): shows a 6-digit code. They open mywunderland.com, type their mobile number, then the code, and pick their PIN. It works once, for 15 minutes. They need a mobile number on file.
- **Reset PIN** (or **Reset PIN (locked)** after 5 wrong tries): removes their PIN and signs them out. Next time they sign in they get a new code (by text, or from you) and pick a new PIN.

> Never ask anyone for their PIN, and don't write it down for them.
`,
  },
  {
    id: 'availability-desk', group: 'Employees', title: 'Availability and weekly limits', who: DESK, where: ['desk'],
    body: `
The **Availability** button on each row opens that person's weekly availability. It reads "(not set)" if they haven't filled it in, and shows their limit, for example "max 25h/4d".

Managers (for their stores) and admins can change it:
1. Pick **Preferred**, **Available** or **Not available**.
2. Click or drag across the times. Start on a time already marked that way to clear it.
3. It saves as you paint.

**Most they want to work each week** sets their **Hours** and **Days** limits (blank = no limit). The day board and Week summary warn you when a schedule goes over them.

The employee then sees a notice on their Today screen saying who changed it and when, with a Dismiss button. HR can look but not change it.

On the day board, the same shading shows on each person's row: green = prefers, red = not available.
`,
  },
  {
    id: 'attendance', group: 'Employees', title: 'Attendance', who: DESK, where: ['desk'],
    body: `
The small **Attendance** button under each name counts the last 60 days:
- **Red number**: their own late arrivals, leaving early and call-outs.
- **Blue number**: times the store cut them for slow: sent home early, asked to start later, or shift cancelled.

Click it to see the details in three groups:
- **Late or left early**: their own reasons (Sick, Personal, Injury).
- **Called out**: missed the whole shift, not counting shifts the store cancelled for slow.
- **Cut for slow**: never counts against them. It's there so you can share slow-day cuts out fairly instead of always asking the same person.

Each line shows the day, what happened, the reason, the store and who marked it. It all comes from [[midday]]. Changes that were undone aren't counted.
`,
  },
  {
    id: 'archive', group: 'Employees', title: 'When someone leaves: archiving', who: ['manager', 'admin'], where: ['desk'],
    body: `
1. Press **Archive** on their row.
2. Pick the **Reason** (Quit, Terminated, Seasonal job ended, Other) and **Off the schedule from** (any date up to a year ahead; pick a past date if they've already left).
3. Press **Archive**.

- **Today or earlier:** they come off the list and are signed out everywhere at once.
- **A later date:** they stay on the list with a **Leaving 11/15** tag and can still sign in until then. **Cancel archive** keeps them.
- Either way, their shifts from that date on come off the schedule straight away (never from weeks before this one). Cancelling an archive doesn't put those shifts back.

Nothing is deleted. **Show archived** at the bottom lists past employees with their history. Search by name or number, and press **Restore** to bring someone back.
`,
  },
  {
    id: 'transfer', group: 'Employees', title: 'Transferring someone to another store', who: ['admin'], where: ['desk'],
    body: `
Only admins can transfer people.
1. Press **Transfer** on their row.
2. Pick where they're moving (**Move to**) and the **Starting** date (this week on, up to 6 months ahead).
3. Press **Transfer…**, check the summary, then press **Transfer**.

From that date their hours count at the new store, and they leave the old store's list. Stations ticked by hand carry over to stations with the same name. Shifts already on a schedule stay where they are and show as shared, so nothing disappears. Until the date, their row shows **Moves to … 11/3**.
`,
  },
  {
    id: 'timeoff-desk', group: 'Employees', title: 'Time-off requests', who: DESK, where: ['desk'],
    body: `
Employees ask for time off in their app. Requests show in the **Time-off requests** box at the top of the Employees page, and on the phone Store tab.

- **Approve**: unpaid time off. It shows on the day board. If they're already scheduled then, their shift gets a purple underline on the day board so you can move it.
- **Approve as sick pay**: only for illness or medical reasons. The hours go on the payroll list below.
- **Deny**

The employee sees the answer as a notice on their Today screen, and a number on their Time off tab. No text is sent.

> You can't answer your own request. Another manager or an admin does that.
`,
  },
  {
    id: 'payroll-box', group: 'Employees', title: 'Time off for payroll', who: DESK, where: ['desk'],
    body: `
The **Time off for payroll** box on the Employees page lists, week by week (‹ ›), everything that has to be entered in PrismHR by hand:
- **Sick pay**: someone left or called out sick, or time off was approved as sick pay.
- **Unpaid time off**: approved time off.

After entering one in PrismHR, tick **Entered in PrismHR**. Admins, HR and the store manager (level 9) can tick these. The totals show how many are still to enter.
`,
  },
  // ---------------- Messages
  {
    id: 'messages-staff', group: 'Messages', title: 'Messages with employees', who: DESK, where: ['desk'], pages: ['messages'],
    body: `
Employees write to their managers from the app. Each employee can pick **All Salem managers** or one manager. Each choice is its own conversation.

- **Private** (to one manager): only that manager, the employee and admins can see it. Other managers and HR can't.
- **All managers**: every manager at that store and admins see it.

### Reading and replying
1. Open **Messages** and pick a conversation on the left, or start one with **New message to…**.
2. When there's more than one conversation with that person, use the buttons above it: **Private · Avery Kim (you) & Taylor** or **All Salem managers**.
3. Type your reply and press **Send message**. Leave **Also text … that they have a message** ticked to send them a short text (replies to that text aren't read).

Under each message you'll see whether a text notice went out and was delivered, or why not ("No mobile number", "Said no to texts", "Replied STOP"). A failed one has **Retry text**.

Admins see every conversation at every store. HR has its own private conversations with employees, and replies come back only to HR.
`,
  },
  {
    id: 'message-several', group: 'Messages', title: 'Messaging several people at once', who: DESK, where: ['desk'],
    body: `
**Message several people…** sends the same message to a group, for example "Staff meeting Sunday at 9 AM".

1. Use a quick pick (**Everyone**, **Working today**, **Working this week**, **Leads & managers (6–9)**) or tick people one by one.
2. Type the message and choose whether to also text them.
3. Press **Send to … people…** and confirm.

Each person gets it in their own conversation, so replies come back to you one by one and nobody sees anyone else's answer.
`,
  },
  {
    id: 'message-hr', group: 'Messages', title: 'Messaging HR', who: ['manager', 'admin', 'hr'], where: ['desk'],
    body: `
Managers and admins can write to HR privately: open **Messages**, then **Message HR**. Only you and HR can read that conversation. Other managers and admins can't.

HR sees these conversations in its list next to its conversations with employees, and replies there.
`,
  },
  {
    id: 'text-alerts', group: 'Messages', title: 'My text alerts and quiet hours', who: DESK, where: ['desk'],
    body: `
Open **My text alerts** on the Messages page to choose when you get a text about new messages. Each person sets their own.

1. Type **My mobile number**.
2. Under **Text me when an employee writes**, choose what to get:
   - Managers: messages sent to you or to all your store's managers (one setting), or off.
   - Admins: only HR's replies to you, or every message.
   - HR: every message sent to HR.
3. Optional: tick **Quiet hours** and pick from and to, for example 10 PM to 8 AM (store time).

Messages that arrive during quiet hours still show in the app straight away. Their texts are held and sent as one combined text when quiet hours end. The message shows "Waiting for quiet hours" until then.
`,
  },
  // ---------------- Settings
  {
    id: 'settings', group: 'Settings', title: 'Settings: stations and rotation rules', who: DESK, where: ['desk'], pages: ['settings'],
    body: `
Settings holds each store's stations and the rules the rotation follows. **Only admins can change it**, and changes save as you go. Managers and HR can view it. Point at the **i** next to any heading for an explanation.

### The station list (filled top to bottom each hour)
- **Order**: ↑ and ↓ move a station up or down.
- **Station or attraction**: click to rename (it changes everywhere).
- **Levels that get it**: click a level to give it that station. On stations needing a ★ for the first spot, click again for "★".
- **Standard day** and **Busy day**: how many people it needs.
- **Takes overflow**: "Yes" means extra people can go here once every station is filled. **Max** is a hard cap that's never exceeded.
- **Out of service**: with from and until dates (blank until = until reopened).
- **Rules**:
  - **Full hour only**
  - **Only when needed** (see [[party-hosts]])
  - **Fill all spots first**: the rotation fills every spot here before spreading people over the other stations (Go Karts by default)
  - **Share group** 1, 2 or 3 (see [[share-groups]])
  - **First spot needs ★**
  - **Highest level first**: Manager and Shift Lead always work this way (by level, equal levels taking turns)
  - **Breaks**: how many people at this station can be on break at once
  - **On break that hour**: Prefer #1–#8, No preference, Avoid, Avoid more or Last resort only. This decides where people on a break are placed.

### Adding and removing
- Type a name in **New station or attraction** and press **Add**. New stations start with no levels ticked.
- **Remove** takes a station off. Its history is kept, and **Restore** under "Removed stations" brings it back. Manager and Shift Lead are always needed and can't be removed.

Below the list: the default number of people for each share group, and the **Level key** showing which stations each level gets.

### Customer receipts
The **Customer receipts** box sets what a customer sees at the top and bottom of a receipt emailed or printed from **Find a receipt**: the store name, address (one line each), phone, website and a closing line. Admins change it and press **Save receipt details**; until then it uses suggestions.

Under **Receipt printer**, admins note the store's receipt printer: its **Network address** (for example 172.16.21.55), its **Printer name on the store computers** (for example BIXOLONReceipt, as it shows in Windows) and **Model**, then press **Save printer**. People printing a receipt are told which printer to pick.
`,
  },
];
